import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  TextareaAutosize,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { RiArrowLeftLine } from "react-icons/ri";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { fetchAssetCategory } from "../../../store/slices/assetCategorySlice";
import {
  editPurchaseRequest,
  fetchPurchaseRequestById,
} from "../../../store/slices/purchaseRequestSlice";
import { successToast } from "../../../response/successToast";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";

// STATUS LIST OPTIONS
const statusList = [
  { value: "requested", label: "Requested" },
  { value: "under_process", label: "Under Process" },
  { value: "procured", label: "Procured" },
  { value: "rejected", label: "Rejected" },
];

const PurchaseRequestEdit = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  let { state } = location;
  if (state === null) {
    state = {
      value: 0,
      currentPage: 1,
      rowperPage: 10,
      searchRequest: "",
      asset_company: "0",
    };
  }
  const [loader, setLoader] = useState(false);
  const [assetCategoryList, setAssetCategoryList] = useState([]);
  const [data, setData] = useState([]);
  const { id } = useParams();
 const companyList = [
    { value: 1, label: "Crest Infosystems Pvt. Ltd." },
    { value: 2, label: "Crest Skillserve Pvt. Ltd." },
  ];
  const validationSchema = Yup.object().shape({
    asset_category_id: Yup.string().required("Please select asset category"),
    title: Yup.string().required("please enter purchase request title"),
    description: Yup.string().required("Please enter description"),
    status: Yup.string().required("This is required field"),
    comments: Yup.string().required("Please Enter Comments"),
  });

  useEffect(() => {
    formik.setValues({
      asset_category_id: data?.asset_category_id,
      asset_company_id:data?.asset_company_id,
      title: data?.title,
      description: data?.description,
      status: data?.status,
      comments: data?.comments,
      request_date: new Date(data?.request_date),
    });
  }, [data]);

  const formik = useFormik({
    initialValues: {
      asset_category_id: data?.asset_category_id,
      asset_company_id:data?.asset_company_id,
      title: data?.title,
      description: data?.description,
      status: data?.status,
      comments: data?.comments,
      request_date: new Date(data?.request_date),
    },
    validationSchema,
    onSubmit: (values) => {
      try {
        setLoader(true);
        dispatch(editPurchaseRequest(values, id)).then((response) => {
          if (response?.success) {
            successToast(
              response?.message || "Purchase Request Updated Successfully"
            );
            navigate("/purchase-request", {
              state: {
                value: state.value,
                currentPage: state.currentPage,
                searchRequest: state.searchRequest,
                rowperPage: state.rowperPage,
                asset_company:state.asset_company
              },
            });
          } else {
            console.error("Failed! Something went wrong.");
          }
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoader(false);
      }
    },
  });

  const fetchAssetCategoryList = async () => {
    setLoader(true);
    try {
      const listing = true;
      const response = await dispatch(fetchAssetCategory(1, "", listing));

      if (response?.success) {
        const formattedOptions = response?.data?.results?.map((category) => ({
          value: category.id,
          label: category.name,
        }));
        setAssetCategoryList(formattedOptions);
      } else {
        console.error("Failed! Something went wrong.");
      }
    } catch (error) {
      console.error("API call error:", error);
    } finally {
      setLoader(false);
    }
  };

  const fetchData = async () => {
    setLoader(true);
    try {
      const response = await dispatch(fetchPurchaseRequestById(id));
      if (response?.success) {
        setData(response?.data);
      } else {
        console.error("Failed! Something went wrong.");
      }
    } catch (error) {
      console.error("API call error:", error);
    } finally {
      setLoader(false);
    }
  };

  const getFieldProps = (field) => ({
    id: `outlined-basic-${field.name}`,
    fullWidth: true,
    variant: "outlined",
    size: "small",
    placeholder: field.placeholder || `Enter ${field.label}`,
    name: field.name,
    value: formik.values[field.name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: formik.touched[field.name] && Boolean(formik.errors[field.name]),
    helperText: formik.touched[field.name] && formik.errors[field.name] && (
      <FormHelperText className="error-message">
        {formik.errors[field.name]}
      </FormHelperText>
    ),
  });

  useEffect(() => {
    fetchAssetCategoryList();
    fetchData();
  }, [id]);

  return (
    <>
      <Box className="content-header">
        <Typography variant="h2" component="h2" className="title-with-btn">
          <Button
            variant="outlined"
            className="action-btn back-btn"
            startIcon={<RiArrowLeftLine />}
            disableRipple
            onClick={() =>
              navigate("/purchase-request", {
                state: {
                  value: state.value,
                  currentPage: state.currentPage,
                  searchRequest: state.searchRequest,
                  rowperPage: state.rowperPage,
                  asset_company:state.asset_company
                },
              })
            }
          ></Button>
          Edit Asset Purchase Request
        </Typography>
      </Box>

      <Box className="content-wrapper">
        <Box className="card-wrapper">
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
               <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography variant="body1" component="label">
                  Title
                  <Typography
                    variant="body1"
                    component="label"
                    className="asterisk"
                  >
                    *
                  </Typography>
                </Typography>
                <TextField
                  className="input-field"
                  fullWidth
                  id="outlined-basic"
                  label=""
                  variant="outlined"
                  size="small"
                  placeholder="Enter title"
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={
                    formik.touched.title && (
                      <span className="error-message">
                        {formik.errors.title}
                      </span>
                    )
                  }
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="body1" component="label">
                  Asset Category
                  <Typography
                    variant="body1"
                    component="label"
                    className="asterisk"
                  >
                    *
                  </Typography>
                </Typography>
                <Select
                  placeholder="Select Asset Category"
                  // isMulti
                  name="asset_category_id"
                  options={assetCategoryList}
                  className={`muilt-select-field ${
                    formik.touched.asset_category_id &&
                    formik.errors.asset_category_id
                      ? "has-error"
                      : ""
                  }`}
                  classNamePrefix="select"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 10,
                    colors: {
                      ...theme.colors,
                      primary25: "var(--secondary-text-color)",
                      primary: "var(--green)",
                    },
                  })}
                  onBlur={formik.handleBlur}
                  onChange={(selectedOption) => {
                    formik.setFieldValue(
                      "asset_category_id",
                      selectedOption?.value
                    );
                    formik.setFieldTouched("asset_category_id", true);
                  }}
                  value={assetCategoryList.find(
                    (option) => option.value === formik.values.asset_category_id
                  )}
                  isSearchable //optional
                />
                {formik.touched.asset_category_id &&
                  formik.errors.asset_category_id && (
                    <FormHelperText className="error-message">
                      {formik.errors.asset_category_id}
                    </FormHelperText>
                  )}
              </Grid>
               <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="body1" component="label">
                  Asset Company
                  <Typography
                    variant="body1"
                    component="label"
                    className="asterisk"
                  >
                    *
                  </Typography>
                </Typography>
                <Select
                  placeholder="Select Asset Company"
                  name="asset_company"
                  options={companyList}
                  className={`muilt-select-field ${
                    formik.touched.asset_company_id && formik.errors.asset_company_id
                      ? "has-error"
                      : ""
                  }`}
                  classNamePrefix="select"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 10,
                    colors: {
                      ...theme.colors,
                      primary25: "var(--secondary-text-color)",
                      primary: "var(--green)",
                    },
                  })}
                  onBlur={formik.handleBlur}
                  onChange={(selectedOption) => {
                    formik.setFieldValue(
                      "asset_company_id",
                      selectedOption?.value
                    );
                    formik.setFieldTouched("asset_company_id", false);
                  }}
                  value={companyList.find(
                    (option) => option.value === formik.values.asset_company_id
                  )}
                  isSearchable // Optional: Enable searching in the dropdown
                />
                {formik.touched.asset_company_id &&
                  formik.errors.asset_company_id && (
                    <FormHelperText className="error-message">
                      {formik.errors.asset_company_id}
                    </FormHelperText>
                  )}
              </Grid>
             
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography variant="body1" component="label">
                  Asset Description
                  <Typography
                    variant="body1"
                    component="label"
                    className="asterisk"
                  >
                    *
                  </Typography>
                </Typography>
                <TextField
                  multiline
                  rows={3}
                  className="input-field"
                  {...getFieldProps({
                    name: "description",
                    required: true,
                    placeholder: "Add Asset Description",
                  })}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="body1" component="label">
                  Purchase Request Date
                  <Typography
                    variant="body1"
                    component="label"
                    className="asterisk"
                  >
                    *
                  </Typography>
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        disabled
                        fullWidth
                        id="outlined-basic-date"
                        label="Purchase Date"
                        variant="outlined"
                        size="small"
                        placeholder="Select Assign Date"
                        name="request_date"
                        value={formik.values.request_date}
                        onBlur={() =>
                          formik.setFieldTouched("request_date", true)
                        }
                      />
                    )}
                    maxDate={new Date(moment())}
                    format="dd-MM-yyyy"
                    className={`input-field ${
                      formik.touched.request_date && formik.errors.request_date
                        ? "error"
                        : ""
                    }`}
                    value={formik.values.request_date}
                    onChange={(date) => {
                      formik.setFieldValue("request_date", date);
                    }}
                  />
                  {formik.touched.request_date &&
                    formik.errors.request_date && (
                      <FormHelperText className="error-message">
                        {formik.errors.request_date}
                      </FormHelperText>
                    )}
                </LocalizationProvider>
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="body1" component="label">
                  Status
                  <Typography
                    variant="body1"
                    component="label"
                    className="asterisk"
                  >
                    *
                  </Typography>
                </Typography>
                <Select
                  placeholder="Select Status"
                  name="status"
                  options={statusList}
                  className={`muilt-select-field ${
                    formik.touched.status && formik.errors.status
                      ? "has-error"
                      : ""
                  }`}
                  classNamePrefix="select"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 10,
                    colors: {
                      ...theme.colors,
                      primary25: "var(--secondary-text-color)",
                      primary: "var(--green)",
                    },
                  })}
                  onBlur={formik.handleBlur}
                  onChange={(selectedOption) => {
                    formik.setFieldValue("status", selectedOption?.value);
                    formik.setFieldTouched("status", true);
                  }}
                  value={statusList.find(
                    (option) => option.value === formik.values.status
                  )}
                  isSearchable //optional
                />
                {formik.touched.status && formik.errors.status && (
                  <FormHelperText className="error-message">
                    {formik.errors.status}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography variant="body1" component="label">
                  Comments
                  <Typography
                    variant="body1"
                    component="label"
                    className="asterisk"
                  >
                    *
                  </Typography>
                </Typography>
                <TextField
                  multiline
                  rows={3}
                  className="input-field"
                  {...getFieldProps({
                    name: "comments",
                    required: true,
                    placeholder: "Add comments",
                  })}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.comments && Boolean(formik.errors.comments)
                  }
                />
              </Grid>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box className="footer-wrapper">
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  className="primary-btn btn w-120"
                  disableRipple
                  disabled={loader}
                  endIcon={
                    loader && <CircularProgress size={20} color="inherit" />
                  }
                >
                  {" "}
                  Save
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  className="secondary-btn btn w-120"
                  disableRipple
                  onClick={() => {
                    navigate("/purchase-request", {
                      state: {
                        value: state.value,
                        currentPage: state.currentPage,
                        searchRequest: state.searchRequest,
                        rowperPage: state.rowperPage,
                        asset_company:state.asset_company
                      },
                    });
                  }}
                >
                  {" "}
                  Cancel
                </Button>
              </Box>
            </Grid>
          </form>
        </Box>
      </Box>
    </>
  );
};

export default PurchaseRequestEdit;
